import type { Allocation, PlanNode } from '@timelog/pm-shared';
import { MAX_PLAN_DEPTH } from '@timelog/pm-shared';

import { applyNodePatch } from './linkedDates';

/**
 * Immutable plan-tree operations. Every op is iterative (BR-3: unbounded
 * depth, never recurse) and path-copying: only nodes on the path to a change
 * are re-created, untouched branches keep referential identity — which is what
 * keeps memoized rows and the rollup cheap.
 *
 * BR-23: ops that deepen the tree refuse to cross MAX_PLAN_DEPTH. The server
 * validates too; these guards exist so the editor never offers an illegal move.
 */

export type InsertPosition = {
  /** null = root level (deliverables). */
  parentId: string | null;
  childIndex: number;
};

type ParentEntry = { parentId: string | null; childIndex: number };

/** One iterative pass: node id → (parent id, index within its siblings). */
const buildParentIndex = (plan: PlanNode[]): Map<string, ParentEntry> => {
  const parents = new Map<string, ParentEntry>();
  type Frame = { node: PlanNode; parentId: string | null; childIndex: number };
  const stack: Frame[] = [];
  for (let index = plan.length - 1; index >= 0; index -= 1) {
    stack.push({ node: plan[index]!, parentId: null, childIndex: index });
  }
  while (stack.length > 0) {
    const { node, parentId, childIndex } = stack.pop()!;
    parents.set(node.id, { parentId, childIndex });
    for (let index = node.children.length - 1; index >= 0; index -= 1) {
      stack.push({ node: node.children[index]!, parentId: node.id, childIndex: index });
    }
  }
  return parents;
};

const findNode = (plan: PlanNode[], id: string): PlanNode | undefined => {
  const stack: PlanNode[] = [...plan];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node.id === id) return node;
    for (const child of node.children) stack.push(child);
  }
  return undefined;
};

/** Levels BELOW `node` (0 for a leaf), iteratively. */
export const subtreeHeight = (node: PlanNode): number => {
  let height = 0;
  const stack: { node: PlanNode; depth: number }[] = [{ node, depth: 0 }];
  while (stack.length > 0) {
    const { node: current, depth } = stack.pop()!;
    if (depth > height) height = depth;
    for (const child of current.children) stack.push({ node: child, depth: depth + 1 });
  }
  return height;
};

/** 0-based depth of a node, or undefined when absent. */
const depthOf = (plan: PlanNode[], id: string): number | undefined => {
  const parents = buildParentIndex(plan);
  if (!parents.has(id)) return undefined;
  let depth = 0;
  let currentId = parents.get(id)!.parentId;
  while (currentId !== null) {
    depth += 1;
    currentId = parents.get(currentId)!.parentId;
  }
  return depth;
};

/** BR-23: would placing `node` under `parentId` push its subtree past the ceiling? */
export const exceedsDepthLimit = (
  plan: PlanNode[],
  node: PlanNode,
  parentId: string | null,
): boolean => {
  if (parentId === null) return subtreeHeight(node) > MAX_PLAN_DEPTH - 1;
  const parentDepth = depthOf(plan, parentId);
  if (parentDepth === undefined) return false;
  return parentDepth + 1 + subtreeHeight(node) > MAX_PLAN_DEPTH - 1;
};

/** Root-to-node path as child indices; undefined when the id is absent. */
const pathTo = (plan: PlanNode[], id: string): number[] | undefined => {
  const parents = buildParentIndex(plan);
  if (!parents.has(id)) return undefined;
  const path: number[] = [];
  let currentId: string | null = id;
  while (currentId !== null) {
    const entry: ParentEntry = parents.get(currentId)!;
    path.push(entry.childIndex);
    currentId = entry.parentId;
  }
  path.reverse();
  return path;
};

/**
 * Rebuilds the spine along `path`, applying `transform` to the node at its
 * end. `transform` returning the same node is a no-op (same plan reference).
 */
const transformAtPath = (
  plan: PlanNode[],
  path: number[],
  transform: (node: PlanNode) => PlanNode,
): PlanNode[] => {
  const spine: PlanNode[] = [];
  let siblings: PlanNode[] = plan;
  for (const index of path) {
    const node = siblings[index]!;
    spine.push(node);
    siblings = node.children;
  }

  const target = spine[spine.length - 1]!;
  let replacement = transform(target);
  if (replacement === target) return plan;

  for (let level = spine.length - 2; level >= 0; level -= 1) {
    const parent = spine[level]!;
    const children = [...parent.children];
    children[path[level + 1]!] = replacement;
    replacement = { ...parent, children };
  }

  const roots = [...plan];
  roots[path[0]!] = replacement;
  return roots;
};

/** Replaces the child list at a position's parent (or the root list). */
const withChildrenAt = (
  plan: PlanNode[],
  parentId: string | null,
  transform: (children: PlanNode[], parent: PlanNode | null) => PlanNode[],
): PlanNode[] => {
  if (parentId === null) {
    return transform(plan, null);
  }
  const path = pathTo(plan, parentId);
  if (!path) return plan;
  return transformAtPath(plan, path, (parent) => ({
    ...parent,
    children: transform(parent.children, parent),
  }));
};

export type NodePatch = Partial<Omit<PlanNode, 'id' | 'children'>>;

/**
 * Both field writers merge through `applyNodePatch`, never a bare spread: it
 * is the one gate that keeps `endDate` and `targetDate` in step (ADR 0051).
 */
export const updateNodeFields = (plan: PlanNode[], id: string, patch: NodePatch): PlanNode[] => {
  const path = pathTo(plan, id);
  if (!path) return plan;
  return transformAtPath(plan, path, (node) => applyNodePatch(node, patch));
};

/**
 * Applies a DIFFERENT patch to each of many nodes in ONE post-order rebuild.
 *
 * Not a loop over `updateNodeFields`: that walks the whole tree per node (via
 * `pathTo` → `buildParentIndex`), so patching k nodes costs O(n·k). A timeline
 * group-drag patches every scheduled descendant of a deliverable on each day the
 * pointer crosses — over a 2,000-leaf subtree in a 10k-node plan that is tens of
 * millions of node visits per frame. This is one O(n) pass whatever k is.
 *
 * Ids that are not in the plan are ignored. Branches with no patch beneath them
 * keep referential identity, so the plan reference only changes when something
 * actually did — which is what lets the reducer's `withPlan` guard skip a no-op.
 */
export const updateManyNodeFields = (
  plan: PlanNode[],
  patchById: ReadonlyMap<string, NodePatch>,
): PlanNode[] => {
  if (patchById.size === 0) return plan;

  type Frame = { node: PlanNode; childIndex: number; children: PlanNode[]; changed: boolean };

  const roots: PlanNode[] = [];
  let rootsChanged = false;

  for (const root of plan) {
    const stack: Frame[] = [{ node: root, childIndex: 0, children: [], changed: false }];
    let completed: PlanNode | null = null;

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;
      if (completed !== null) {
        frame.children.push(completed);
        completed = null;
      }
      if (frame.childIndex < frame.node.children.length) {
        const child = frame.node.children[frame.childIndex]!;
        frame.childIndex += 1;
        stack.push({ node: child, childIndex: 0, children: [], changed: false });
        continue;
      }

      stack.pop();
      const patch = patchById.get(frame.node.id);
      let rebuilt = frame.node;
      if (frame.changed) rebuilt = { ...rebuilt, children: frame.children };
      // An empty patch must not fabricate a new object — that would bump the
      // document revision and trigger a save for nothing.
      if (patch !== undefined && Object.keys(patch).length > 0)
        rebuilt = applyNodePatch(rebuilt, patch);

      if (rebuilt !== frame.node && stack.length > 0) stack[stack.length - 1]!.changed = true;
      if (stack.length === 0) {
        roots.push(rebuilt);
        if (rebuilt !== root) rootsChanged = true;
      } else {
        completed = rebuilt;
      }
    }
  }

  return rootsChanged ? roots : plan;
};

/**
 * Inserts a node. BR-1: when the parent was a leaf carrying an estimate or
 * allocations, they move onto the new child (a parent is a pure aggregate) —
 * deterministic and lossless; flagged for product confirmation.
 */
export const insertNode = (
  plan: PlanNode[],
  position: InsertPosition,
  node: PlanNode,
): PlanNode[] => {
  if (exceedsDepthLimit(plan, node, position.parentId)) return plan;
  return withChildrenAt(plan, position.parentId, (children) => {
    const index = Math.max(0, Math.min(position.childIndex, children.length));
    return [...children.slice(0, index), node, ...children.slice(index)];
  });
};

/**
 * insertNode plus the BR-1 hand-off when parenting a leaf: the parent's
 * estimate/allocations always clear (a parent is a pure aggregate), and they
 * transfer onto the new child only when it brings none of its own — a child
 * with its own hours defines the subtree, and BR-1 forbids mixing.
 */
export const insertChildWithTransfer = (
  plan: PlanNode[],
  parentId: string,
  childIndex: number,
  node: PlanNode,
): PlanNode[] => {
  const path = pathTo(plan, parentId);
  if (!path) return plan;
  return transformAtPath(plan, path, (parent) => {
    const wasLeaf = parent.children.length === 0;
    /*
      A leaf becoming a parent must give up both fields (BR-1), so each one
      moves down to the child unless the child already brings its own. The two
      are decided SEPARATELY: they are independent quantities (BR-28), so a
      child arriving with allocations but no budget still inherits the budget.
      This used to demand the child be empty of *both* before either moved,
      which silently discarded the parent's budget in that case.
    */
    const transferEstimate =
      wasLeaf && node.estimatedHours === undefined && parent.estimatedHours !== undefined;
    const transferAllocations =
      wasLeaf && node.allocations.length === 0 && parent.allocations.length > 0;

    const child: PlanNode = {
      ...node,
      estimatedHours: transferEstimate ? parent.estimatedHours : node.estimatedHours,
      allocations: transferAllocations ? parent.allocations : node.allocations,
    };
    /*
      The leaf's billing fields go the same way (BR-2): a parent with children
      aggregates and may not carry `contractId`, `roleRef`, `assignee` or
      `billable` — the API refuses the document if a root does. They move down
      to the child unless it brings its own; the child is what gets priced now.
      Without this, mapping a top-level task to a contract and then adding a
      subtask under it failed to save.
    */
    for (const field of ['contractId', 'roleRef', 'assignee', 'billable'] as const) {
      if (wasLeaf && child[field] === undefined && parent[field] !== undefined) {
        Object.assign(child, { [field]: parent[field] });
      }
    }

    const index = Math.max(0, Math.min(childIndex, parent.children.length));
    const next: PlanNode = {
      ...parent,
      children: [...parent.children.slice(0, index), child, ...parent.children.slice(index)],
    };
    if (wasLeaf && parent.estimatedHours !== undefined) delete next.estimatedHours;
    if (wasLeaf && parent.allocations.length > 0) next.allocations = [];
    if (wasLeaf) {
      delete next.contractId;
      delete next.roleRef;
      delete next.assignee;
      delete next.billable;
    }
    return next;
  });
};

/** Drops ids whose ancestor is also selected — ops act on whole subtrees once. */
export const topmostOnly = (plan: PlanNode[], ids: ReadonlySet<string>): Set<string> => {
  const parents = buildParentIndex(plan);
  const result = new Set<string>();
  for (const id of ids) {
    if (!parents.has(id)) continue;
    let ancestor = parents.get(id)!.parentId;
    let covered = false;
    while (ancestor !== null) {
      if (ids.has(ancestor)) {
        covered = true;
        break;
      }
      ancestor = parents.get(ancestor)!.parentId;
    }
    if (!covered) result.add(id);
  }
  return result;
};

/** Removes every selected subtree in one structural-sharing rebuild pass. */
export const removeSubtrees = (plan: PlanNode[], ids: ReadonlySet<string>): PlanNode[] => {
  const doomed = topmostOnly(plan, ids);
  if (doomed.size === 0) return plan;

  // Post-order rebuild with explicit frames; children arrays are only copied
  // when something beneath them changed.
  type Frame = { node: PlanNode; childIndex: number; children: PlanNode[]; changed: boolean };

  const rebuildList = (list: PlanNode[]): { list: PlanNode[]; changed: boolean } => {
    const out: PlanNode[] = [];
    let changed = false;

    for (const root of list) {
      if (doomed.has(root.id)) {
        changed = true;
        continue;
      }
      // Iteratively rebuild this root.
      const stack: Frame[] = [{ node: root, childIndex: 0, children: [], changed: false }];
      let completed: PlanNode | null = null;
      while (stack.length > 0) {
        const frame = stack[stack.length - 1]!;
        if (completed !== null) {
          frame.children.push(completed);
          completed = null;
        }
        if (frame.childIndex < frame.node.children.length) {
          const child = frame.node.children[frame.childIndex]!;
          frame.childIndex += 1;
          if (doomed.has(child.id)) {
            frame.changed = true;
            continue;
          }
          stack.push({ node: child, childIndex: 0, children: [], changed: false });
        } else {
          stack.pop();
          const rebuilt = frame.changed ? { ...frame.node, children: frame.children } : frame.node;
          if (frame.changed && stack.length > 0) stack[stack.length - 1]!.changed = true;
          if (stack.length === 0) {
            completed = null;
            out.push(rebuilt);
            if (rebuilt !== root) changed = true;
          } else {
            completed = rebuilt;
          }
        }
      }
    }

    return { list: out, changed };
  };

  const { list, changed } = rebuildList(plan);
  return changed ? list : plan;
};

/** Iterative deep copy with fresh ids; codes stripped (server reassigns, BR-6). */
export const cloneSubtree = (
  node: PlanNode,
  makeId: () => string,
  renameRoot?: (name: string) => string,
): PlanNode => {
  const copyShallow = (source: PlanNode, isRoot: boolean): PlanNode => {
    const copy: PlanNode = {
      ...source,
      id: makeId(),
      name: isRoot && renameRoot ? renameRoot(source.name) : source.name,
      allocations: source.allocations.map((allocation): Allocation => ({
        ...allocation,
        id: makeId(),
      })),
      children: [],
    };
    delete copy.code;
    return copy;
  };

  const rootCopy = copyShallow(node, true);
  type Frame = { source: PlanNode; copy: PlanNode; childIndex: number };
  const stack: Frame[] = [{ source: node, copy: rootCopy, childIndex: 0 }];

  while (stack.length > 0) {
    const frame = stack[stack.length - 1]!;
    if (frame.childIndex < frame.source.children.length) {
      const child = frame.source.children[frame.childIndex]!;
      frame.childIndex += 1;
      const childCopy = copyShallow(child, false);
      frame.copy.children.push(childCopy);
      stack.push({ source: child, copy: childCopy, childIndex: 0 });
    } else {
      stack.pop();
    }
  }

  return rootCopy;
};

/** Duplicates each selected subtree right after its source (topmost-only). */
export const duplicateSubtrees = (
  plan: PlanNode[],
  ids: ReadonlySet<string>,
  makeId: () => string,
): PlanNode[] => {
  const targets = topmostOnly(plan, ids);
  let result = plan;
  for (const id of targets) {
    const parents = buildParentIndex(result);
    const entry = parents.get(id);
    if (!entry) continue;
    const source = findNode(result, id)!;
    const copy = cloneSubtree(source, makeId, (name) => `${name} (copy)`);
    result = withChildrenAt(result, entry.parentId, (children) => [
      ...children.slice(0, entry.childIndex + 1),
      copy,
      ...children.slice(entry.childIndex + 1),
    ]);
  }
  return result;
};

/** True when `candidateId` is inside the subtree rooted at `rootId`. */
export const isInSubtree = (plan: PlanNode[], rootId: string, candidateId: string): boolean => {
  if (rootId === candidateId) return true;
  const root = findNode(plan, rootId);
  if (!root) return false;
  const stack: PlanNode[] = [...root.children];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node.id === candidateId) return true;
    for (const child of node.children) stack.push(child);
  }
  return false;
};

/**
 * Moves a subtree to a new position. Rejects moves into the subtree itself
 * (returns the original plan). The target position is resolved AFTER removal,
 * expressed as parent + index in that parent's post-removal child list.
 * Inserting under a leaf goes through the BR-1 transfer so the new parent
 * never keeps an estimate alongside children.
 */
export const moveSubtree = (
  plan: PlanNode[],
  sourceId: string,
  target: InsertPosition,
): PlanNode[] => {
  if (target.parentId !== null && isInSubtree(plan, sourceId, target.parentId)) return plan;
  const source = findNode(plan, sourceId);
  if (!source) return plan;
  if (exceedsDepthLimit(plan, source, target.parentId)) return plan;

  /*
    BR-2 is a property of WHERE a node sits, not of what it used to be: a root
    node is a deliverable, anything nested is a task. The Planning UI has no
    Deliverable concept (ADR 0028) — on screen a deliverable and a parent task
    are the same thing — so the destination decides the kind. Before this, a
    root node simply refused to nest, which made the whole gesture impossible
    in a NEW project, where every row is a root.

    Only the moved node is touched. Its descendants are already tasks, and they
    stay tasks wherever the subtree lands.
  */
  const kind: PlanNode['kind'] = target.parentId === null ? 'deliverable' : 'task';
  const moved = source.kind === kind ? source : { ...source, kind };

  const without = removeSubtrees(plan, new Set([sourceId]));
  if (target.parentId === null) {
    const index = Math.max(0, Math.min(target.childIndex, without.length));
    return [...without.slice(0, index), moved, ...without.slice(index)];
  }
  return insertChildWithTransfer(without, target.parentId, target.childIndex, moved);
};

/**
 * Indent: the node becomes the last child of its previous sibling, and a task
 * by virtue of landing there (BR-2). The previous sibling must exist.
 */
export const indentNode = (plan: PlanNode[], id: string): PlanNode[] => {
  const node = findNode(plan, id);
  // No kind guard: a root row indents into its previous sibling and becomes a
  // task, the same conversion `moveSubtree` makes. Refusing on `kind` left
  // Alt+→ dead on every row of a new project.
  if (!node) return plan;
  const parents = buildParentIndex(plan);
  const entry = parents.get(id);
  if (!entry || entry.childIndex === 0) return plan;

  const siblings =
    entry.parentId === null ? plan : (findNode(plan, entry.parentId)?.children ?? []);
  const previousSibling = siblings[entry.childIndex - 1];
  if (!previousSibling) return plan;
  if (exceedsDepthLimit(plan, node, previousSibling.id)) return plan;

  const without = removeSubtrees(plan, new Set([id]));
  // Nested ⇒ a task, whatever it was at root.
  const nested = node.kind === 'task' ? node : { ...node, kind: 'task' as const };
  return insertChildWithTransfer(without, previousSibling.id, Number.MAX_SAFE_INTEGER, nested);
};

/**
 * Outdent: the node becomes the next sibling of its parent (BR-2: depth-1
 * tasks stay tasks — outdenting to root is refused).
 */
export const outdentNode = (plan: PlanNode[], id: string): PlanNode[] => {
  const parents = buildParentIndex(plan);
  const entry = parents.get(id);
  if (!entry || entry.parentId === null) return plan;
  const parentEntry = parents.get(entry.parentId)!;

  const node = findNode(plan, id)!;
  const without = removeSubtrees(plan, new Set([id]));
  /*
    Outdenting out of a root-level parent lands the node AT root, where BR-2
    makes it a deliverable. This used to be refused outright, which is why a
    task could be nested but never dragged back out to top level.
  */
  const kind: PlanNode['kind'] = parentEntry.parentId === null ? 'deliverable' : 'task';
  const moved = node.kind === kind ? node : { ...node, kind };
  // After removal the parent's own index is unchanged (we removed a child).
  return insertNode(
    without,
    {
      parentId: parentEntry.parentId,
      childIndex: parentEntry.childIndex + 1,
    },
    moved,
  );
};
